import React, { useState, useRef, useEffect, useCallback, KeyboardEvent, useMemo } from 'react';
import _findIndex from 'lodash/findIndex';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import _flatMap from 'lodash/flatMap';
import { useClickOutside } from '../../../hooks/useClickOutside';
import Message from '../common/Message';
import {
  DropdownFieldContainer,
  DropdownSection,
  DropdownListHeader,
  DropdownEmptyState,
} from './DropdownV3.styles';
import {
  MessageContainer,
  Asterisk,
  FormLabel,
  ListBoxOption,
  ListBoxGroupLabel,
} from '../form.styles';
import { debounce, scrollParentToChild, uuidv4 } from './helpers';
import DropdownListBox from '../common/DropdownListBox';
import { DropdownTrigger } from '../common/DropdownTrigger/DropdownTrigger';
import { CustomDropdownTriggerProps } from '../common/DropdownTrigger/CustomDropdownTrigger';
import { BoxDropdownTriggerProps } from '../common/DropdownTrigger/BoxDropdownTrigger';
import { BaseTriggerProps } from '../common/DropdownTrigger/types';

type Option<T extends string | number | null> = {
  label: string;
  value: T;
  style?: React.CSSProperties;
  alternateDisplayLabel?: React.ReactElement;
  key?: string;
  group?: string;
};

const NO_GROUP_KEY = '';
type OrderedGroup<T extends string | number | null = string | number | null> = {
  label: string;
  options: Option<T>[];
};

const sortByGroup = <T extends string | number | null = string | number | null>(
  options: Option<T>[]
): Option<T>[] => {
  const orderedGroups = options.reduce(
    (acc, option) => {
      const currentLabel = option.group || NO_GROUP_KEY;
      const existingGroup = acc.find((group) => group.label === currentLabel);
      if (existingGroup) {
        existingGroup.options.push(option);
      } else {
        acc.push({ label: currentLabel, options: [option] });
      }
      return acc;
    },
    [{ label: NO_GROUP_KEY, options: [] }] as OrderedGroup<T>[]
  );
  return _flatMap(orderedGroups, (group) => group.options);
};

type ModifiedOption<T extends string | number | null> = Option<T> & {
  placeholder?: boolean;
};

type TriggerProps = BaseTriggerProps &
  (
    | CustomDropdownTriggerProps
    | BoxDropdownTriggerProps
    | {
        /**
         * Instead of a dropdown trigger (box) or a custom trigger, render the
         * trigger as a Chip.
         */
        chipTrigger?: {
          withCaret?: boolean;
          label?: string | React.ReactElement;
        };
      }
  );

export type DropdownV3Props<
  T extends string | number | null = string | number | null
> = TriggerProps & {
  /** Any external effect you want to happen when the user selects an item */
  onSelect?: (s: T) => void;
  /** The items in the dropdown */
  options: Option<T>[];
  /** The form label that appears above the select box */
  label?: string;
  /** Make the whole dropdown an inline element, instead of a block */
  inline?: boolean;
  /** A standard class string for the dropdown's outermost div */
  className?: string;
  /** Place the label to the side of the dropdown instead of over it; previously inlineLabel */
  alignment?: 'horizontal' | 'vertical';
  /** Allow user to select a null-value option with the text from the placeholder */
  nullable?: boolean;
  /**
   * Alternative text (different from the placeholder) that goes in the options list
   * when the dropdown is nullable.  If not provided, will default to the placeholder.
   */
  nullOptionLabel?: string;
  /** Split the list of options by their group property */
  withOptionGroups?: boolean;
  /** Content to be rendered at the top within the expanded dropdown box  */
  header?: JSX.Element;
  /** Content to be rendered at the top on mobile view of dropdown */
  mobileHeader?: string | React.ReactNode;
  /** Option to show overlay behind the mobile view of dropdown; height of dropdown will be set to fit-content instead of 100vh */
  mobileOverlay?: boolean;
  /** Empty state to display when no options are available */
  emptyState?: JSX.Element;
  /** Should the dropdown list box be positioned in relation to the input element or its parent? */
  boxAnchor?: 'self' | 'parent';
  /** How should the dropdown list box be aligned  */
  boxAlign?: 'left' | 'center' | 'right';
  /** How wide the dropdown list box should be. By default, it will be 100%. */
  boxWidth?: string | number;
  isRequired?: boolean;

  errorMessage?: string;

  value?: string | number;
  placeholder?: string;
};

/**
 * `import DropdownV3 from '@zola/zola-ui/src/components/Form/dropdownV3/DropdownV3'`
 *
 * To be used standalone. Primary field is an `<input />` field to allow for browser autocomplete.
 *
 * This listbox follows the WAI-ARIA standards listed in https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html for listboxes.
 */
const DropdownV3 = <T extends string | number | null = string | number | null>({
  onSelect,
  options,
  label,
  placeholder = 'Please select',
  inline,
  errorMessage,
  className,
  alignment = 'vertical',
  nullable,
  nullOptionLabel,
  withOptionGroups = false,
  emptyState,
  header,
  mobileHeader,
  mobileOverlay,
  isRequired,
  id,
  value,
  boxAnchor = 'self',
  boxWidth,
  boxAlign,
  disabled,
  ...rest
}: DropdownV3Props<T>): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option<T> | ModifiedOption<T> | null>(null);
  const [prevValue, setPrevValue] = useState<T | null>(null);
  const [search, setSearch] = useState('');
  const [searchMatches, setSearchMatches] = useState<Option<T>[]>([]);
  // Closes dropdown if click outside
  const ref = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setExpanded(false);
      if (selectedOption != null && onSelect) {
        onSelect(selectedOption.value);
      }
    },
    isOpen: expanded,
    exclusion: chipRef,
  });

  function handleOptionSelection(
    option: Option<T> | ModifiedOption<T> | null,
    shouldExpand: boolean
  ) {
    setSelectedOption(option);
    if (onSelect) {
      onSelect((option?.value || null) as T);
    }
    setExpanded(shouldExpand); // toggles the dropdown open and closed
  }

  const clearSearch = () => {
    setSearch('');
    setSearchMatches([]);
  };

  const cycleThroughSearchMatches = (matches: Option<T>[], selected: Option<T>) => {
    const currentIndex = _findIndex(matches, (o) => o.value === selected.value);
    const isLast = currentIndex === matches.length - 1;
    setSelectedOption(
      isLast ? matches[0] : matches[Math.min(currentIndex + 1, matches.length - 1)]
    );
  };

  // Set up debounce on clearing internal search
  const debouncedClear = useCallback(debounce(clearSearch, 500), []);
  useEffect(() => {
    if (!search) return;
    const searchLength = search.length;
    // prioritize first letter in search results
    if (searchLength === 1) {
      const matches = options.filter((o) => o.label[0].toLowerCase() === search.toLowerCase());
      setSearchMatches(matches);
      if (matches.length) {
        // if is a previously existing selectedOption from another function, don't reset to top
        if (selectedOption) {
          cycleThroughSearchMatches(matches, selectedOption);
        } // otherwise set at top of matching array
        else {
          setSelectedOption(matches[0]);
        }
      }
    }
    if (searchLength > 1 && selectedOption) {
      // prioritize same letter cycling i.e. pressing N twice in states list should cycle through states that start with N
      // if last letter typed matches previous letter typed, prioritize cycle
      if (search.charAt(searchLength - 1) === search.charAt(searchLength - 2)) {
        cycleThroughSearchMatches(searchMatches, selectedOption);
      }
      // else try to match string
      else {
        const newSelection = _find(options, (o) => _includes(o.label.toLowerCase(), search));
        if (newSelection) setSelectedOption(newSelection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, search]); // intentionally only watching changes to `search` and `options` to prevent too many updates

  const [modifiedOptions, setModifiedOptions] = useState<ModifiedOption<T>[]>([]);

  // Adds the placeholder to the expanded dropdown options if dropdown can be null,
  // and sorts the options by group (if withOptionGroups is specified)
  useEffect(() => {
    const orderedOptions = withOptionGroups ? sortByGroup(options) : options;
    if (nullable) {
      const nullOption = {
        label: nullOptionLabel || placeholder,
        value: null as T,
        placeholder: true,
      };
      setModifiedOptions([nullOption, ...orderedOptions]);
    } else {
      setModifiedOptions(orderedOptions);
    }
  }, [nullable, options, placeholder, nullOptionLabel, withOptionGroups]);

  // if value is forcibly changed, select the option with new value
  useEffect(() => {
    if (value && value !== prevValue) {
      const foundOption = modifiedOptions.filter((opt) => opt.value === value)[0];
      setSelectedOption(foundOption as Option<T>);
      if (foundOption) {
        setPrevValue(value as T);
      }
    }
    // if form/input is reset eg. had previous value, now has none
    else if (prevValue && value === '') {
      setSelectedOption(null);
    }
  }, [modifiedOptions, prevValue, selectedOption, value]);

  // This is used to give each element in the dropdown a unique Id to facilite
  // scrolling to the element when the dropdown is expanded.  There may be
  // other ways to do this.
  // This shouldn't be needed, id is a required prop, but if its not provided
  // we'll still generate an id for all the dropdown options so they can be
  // scrolled
  const [idPrefix, setIdPrefix] = useState(uuidv4());

  useEffect(() => {
    if (id) {
      setIdPrefix(id);
    }
  }, [id]);

  // if an option is selected and it's not in full view of the user while the dropdown is expanded, scroll to the newly selected option
  useEffect(() => {
    if (!selectedOption) return;
    const currentIndex = _findIndex(
      modifiedOptions,
      (x) => x.value === selectedOption.value && x.group === selectedOption.group
    );
    if (
      (currentIndex || currentIndex === 0) &&
      expanded &&
      document.getElementById(`${idPrefix}-${currentIndex}`)
    ) {
      const parent =
        ref?.current &&
        (ref.current.getElementsByClassName('dropdown-v2__options')[0] as HTMLElement);
      const child = document.getElementById(`${idPrefix}-${currentIndex}`);
      if (parent && child) {
        scrollParentToChild(parent, child);
      }
    }
  }, [selectedOption, expanded, setSelectedOption, modifiedOptions, idPrefix]);

  // handle all key presses required by WAI-ARIA standards
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // helper function for finding the index of the current option.
    const findCurrentIndex = () => {
      if (!selectedOption) return -1;
      return _findIndex(modifiedOptions, (x) => {
        return x.value === selectedOption.value && x.group === selectedOption.group;
      });
    };
    // If ESC, close the dropdown and move focus to next element
    if (e.key === 'Escape') {
      e.preventDefault();
      setExpanded(false);
      const form = (e.target as HTMLInputElement)?.form;
      if (form) {
        const index = Array.prototype.indexOf.call(form, e.target);
        (form.elements[index + 1] as HTMLElement).focus();
        e.preventDefault();
      }
    }
    if (e.key === 'Tab') {
      const currentIndex = findCurrentIndex();
      // DON'T prevent default here, we want
      // the tab key to move the user to the next
      // form element!
      setExpanded(false);
      setSelectedOption(modifiedOptions[currentIndex]);
      handleOptionSelection(selectedOption, false);
    }
    // If HOME, open dropdown and move to first element
    if (e.key === 'Home') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(modifiedOptions[0]);
    }
    // If END, open dropdown and move to last element
    if (e.key === 'End') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(modifiedOptions[modifiedOptions.length - 1]);
    }
    // If UP:
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // if no option has been selected, select last option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(modifiedOptions[modifiedOptions.length - 1]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }
      // Otherwise, move one option up. If at the top of the list, circle back to the bottom
      const currentIndex = findCurrentIndex();
      if (currentIndex === 0) {
        setSelectedOption(modifiedOptions[modifiedOptions.length - 1]);
        return;
      }
      setSelectedOption(modifiedOptions[Math.max(currentIndex - 1, 0)]);
      return;
    }
    // If DOWN
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // If no selected option, select the first option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(modifiedOptions[0]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }

      // Otherwise, move one option down. If at the bottom of the list, circle back to the top
      const currentIndex = findCurrentIndex();
      if (currentIndex === modifiedOptions.length - 1) {
        setSelectedOption(modifiedOptions[0]);
        return;
      }
      setSelectedOption(modifiedOptions[Math.min(currentIndex + 1, modifiedOptions.length - 1)]);
      return;
    }

    // I need a better test here probably, but this seemed like a good first pass
    // If the name of the key is only one letter long, treat it as search input
    if (e.key.length === 1) {
      e.preventDefault();
      setSearch(search + e.key);
      debouncedClear(); // clear the search string if it's been longer than 0.25sec
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // enter key toggles the dropdown open and closed
      handleOptionSelection(selectedOption, !expanded);
    }
  };

  const triggerProps = useMemo(() => {
    if ('chipTrigger' in rest) {
      return {
        chips: true,
        ...rest.chipTrigger,
        chipRef,
        selected: Boolean(selectedOption?.value),
      };
    }
    if ('trigger' in rest) {
      const { trigger, ...customTriggerProps } = rest;
      return {
        value: selectedOption ? selectedOption.label : placeholder,
        isPlaceholder: !selectedOption?.value,
        trigger,
        ...customTriggerProps,
      };
    }
    return {
      value: selectedOption ? selectedOption.label : placeholder,
      isPlaceholder: !selectedOption?.value,
      ...rest,
    };
  }, [placeholder, rest, selectedOption]);

  const hasTrigger = 'trigger' in rest;

  return (
    <DropdownFieldContainer
      ref={ref}
      inline={inline || hasTrigger}
      alignment={alignment}
      className={className}
      hasLabel={Boolean(label)}
      hasMinWidth={!hasTrigger}
      data-state={errorMessage ? 'error' : ''}
    >
      {label && (
        <FormLabel htmlFor={id}>
          {label}
          {isRequired && <Asterisk>*</Asterisk>}
        </FormLabel>
      )}
      <DropdownSection position={boxAnchor === 'parent' ? 'static' : 'relative'}>
        <DropdownTrigger
          handleKeyDown={handleKeyDown}
          toggleDropdown={() => setExpanded(!expanded)}
          expanded={expanded}
          id={id}
          disabled={disabled}
          {...triggerProps}
        />
        {expanded && (modifiedOptions.length > 0 || !!emptyState) && (
          <DropdownListBox
            onClose={() => setExpanded(false)}
            className="dropdown-v2__options"
            width={boxWidth || '100%'}
            align={boxAlign || 'left'}
            mobileHeader={mobileHeader}
            mobileOverlay={mobileOverlay}
          >
            {!!header && <DropdownListHeader>{header}</DropdownListHeader>}
            {modifiedOptions.map((opt, index) => {
              const inGroup = withOptionGroups && !!opt.group;
              const isFirstInGroup =
                inGroup && (index < 1 || opt.group !== modifiedOptions[index - 1]?.group);
              return (
                <React.Fragment key={opt.key || opt.value}>
                  {isFirstInGroup && <ListBoxGroupLabel>{opt.group}</ListBoxGroupLabel>}
                  <ListBoxOption
                    style={opt.style}
                    selected={Boolean(selectedOption?.value === opt.value) && !opt.placeholder}
                    isPlaceholder={opt.placeholder}
                    shouldIndent={inGroup}
                    id={`${idPrefix}-${index}`}
                    onClick={() => {
                      handleOptionSelection(opt, false);
                      setExpanded(false);
                    }}
                  >
                    {opt.alternateDisplayLabel || opt.label}
                  </ListBoxOption>
                </React.Fragment>
              );
            })}
            {modifiedOptions.length < 1 && <DropdownEmptyState>{emptyState}</DropdownEmptyState>}
          </DropdownListBox>
        )}
        {errorMessage && (
          <MessageContainer>
            <Message type="error" text={errorMessage} className="error-message" />
          </MessageContainer>
        )}
      </DropdownSection>
    </DropdownFieldContainer>
  );
};

export default DropdownV3;
